import { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Sparkles, CheckCircle2, ArrowRight, ShoppingCart, Package, Factory, Briefcase, Heart, GraduationCap, Wrench, Building2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useToast } from '../hooks/useToast';
import { api } from '../lib/api';

const ICONS = { retail:ShoppingCart, wholesale:Package, manufacturing:Factory, service:Briefcase, food:ShoppingCart, medical:Heart, education:GraduationCap, automotive:Wrench, other:Building2 };

export default function Onboarding() {
  const { token, user } = useAuth();
  const { refreshProfile } = useTenant();
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [industries, setIndustries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.onboardingIndustries().then(r => setIndustries(r.items||[])).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  if (!token) return <Navigate to="/login" replace />;
  if (user && user.role !== 'owner') return <Navigate to="/home" replace />;

  const confirm = async () => {
    if (!selected || !token) return;
    setSaving(true);
    try { await api.onboardingComplete(token, selected); await refreshProfile(); setStep(2); }
    catch (e) { toast.show(e?.message||'Something went wrong','error'); }
    finally { setSaving(false); }
  };

  const firstName = user?.name?.split(' ')[0] || '';
  const currentInd = industries.find(i => i.id === selected);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1220] via-[#101B33] to-[#1B1435] flex flex-col items-center" data-testid="onboarding-page">
      <div className="flex items-center gap-2 mt-12 mb-8" data-testid="onboarding-progress">
        {[0,1,2].map(i => <div key={i} className={`h-1 rounded-full transition-all ${step===i?'w-10 bg-teal-400':'w-6 bg-white/15'}`} />)}
      </div>

      {step===0 && (
        <div className="w-full max-w-xl px-6 animate-fade-in">
          <p className="text-white/60 text-base mb-1">Hi {firstName}</p>
          <h1 className="text-3xl font-extrabold text-white tracking-tight leading-tight">What kind of business<br />do you run?</h1>
          <p className="text-white/50 mt-3 text-base">We&apos;ll tailor your dashboard, labels and shortcuts to match.</p>
          {loading ? (
            <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <div className="mt-8 space-y-3" data-testid="industry-list">
              {industries.map(ind => {
                const Icon = ICONS[ind.id] || ICONS[ind.icon] || Building2;
                return (
                  <button key={ind.id} data-testid={`industry-card-${ind.id}`} onClick={() => setSelected(ind.id)}
                    className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl border-2 transition-all text-left ${selected===ind.id?'border-teal-400 bg-teal-500/10 shadow-[0_0_0_4px_rgba(14,165,183,0.12)]':'border-white/8 bg-white/[0.04] hover:bg-white/[0.06] hover:border-white/15'}`}>
                    <div className="w-12 h-12 rounded-xl bg-white/[0.08] flex items-center justify-center flex-shrink-0"><Icon size={24} className="text-teal-400" /></div>
                    <div className="flex-1 min-w-0"><p className="text-white font-bold text-base">{ind.display}</p><p className="text-white/50 text-sm mt-0.5 line-clamp-1">{ind.description}</p></div>
                    <ChevronRight size={20} className={selected===ind.id?'text-teal-400':'text-white/30'} />
                  </button>
                );
              })}
            </div>
          )}
          <div className="mt-8 pb-8">
            <button data-testid="onboarding-continue-btn" onClick={() => setStep(1)} disabled={!selected}
              className={`w-full h-14 rounded-2xl text-white font-bold text-base transition-all ${selected?'bg-gradient-to-r from-teal-500 to-cyan-600 hover:shadow-lg hover:shadow-teal-500/25':'bg-white/10 cursor-not-allowed opacity-50'}`}>
              Continue
            </button>
          </div>
        </div>
      )}

      {step===1 && currentInd && (
        <div className="w-full max-w-xl px-6 animate-fade-in">
          <button data-testid="onboarding-back-btn" onClick={() => setStep(0)} className="flex items-center gap-2 text-white/60 hover:text-white text-sm font-medium mb-6 transition-colors">
            <ChevronLeft size={18} /> Back
          </button>
          <div className="text-center mb-8">
            <h1 className="text-2xl font-extrabold text-white tracking-tight">Here&apos;s how we&apos;ll personalize your app</h1>
            <p className="text-white/50 mt-2">Optimized for <span className="text-teal-400 font-bold">{currentInd.display}</span>. You can change this anytime.</p>
          </div>
          <div className="glass-card p-5 mb-4">
            <p className="text-xs text-white/50 uppercase font-semibold tracking-wider mb-3">Selected Industry</p>
            <p className="text-white font-bold text-lg">{currentInd.display}</p>
            <p className="text-white/50 text-sm mt-1">{currentInd.description}</p>
          </div>
          <div className="mt-8 pb-8">
            <button data-testid="onboarding-confirm-btn" onClick={confirm} disabled={saving}
              className="w-full h-14 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold text-base hover:shadow-lg hover:shadow-teal-500/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
              {saving ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin" /> : <><Sparkles size={18} /> Set up my workspace</>}
            </button>
          </div>
        </div>
      )}

      {step===2 && (
        <div className="w-full max-w-md px-6 flex flex-col items-center text-center animate-fade-in pt-8">
          <div className="w-28 h-28 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-[0_16px_32px_rgba(14,165,183,0.4)] mb-8" data-testid="onboarding-check">
            <CheckCircle2 size={56} className="text-white" />
          </div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight" data-testid="onboarding-success-title">You&apos;re all set{firstName?`, ${firstName}`:''}!</h1>
          <p className="text-white/50 mt-3 text-base leading-relaxed">Your workspace is personalized for <span className="text-teal-400 font-bold">{currentInd?.display||'your business'}</span>. Let&apos;s make some money.</p>
          <div className="glass-card p-5 mt-8 w-full text-left space-y-3">
            {['Home tiles renamed and reordered for you','Fraud & market alerts wired to your industry','AI concierge now speaks your business language'].map(t => (
              <div key={t} className="flex items-center gap-3"><CheckCircle2 size={18} className="text-teal-400 flex-shrink-0" /><p className="text-white/80 text-sm font-medium">{t}</p></div>
            ))}
          </div>
          <button data-testid="onboarding-go-home-btn" onClick={() => navigate('/home',{replace:true})}
            className="w-full h-14 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold text-base hover:shadow-lg hover:shadow-teal-500/25 transition-all flex items-center justify-center gap-2 mt-8">
            Go to Dashboard <ArrowRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
}
